import DynoChart from "@/components/DynoChart";
import { ArrowRight, Zap, TrendingUp } from "lucide-react";
import StatCounter from "@/components/StatCounter";

function StatBlock({ label, hp, nm, accent, animate }) {
  return (
    <div className={`p-6 border ${accent ? "border-hp-yellow bg-hp-yellow/[0.06]" : "border-white/10 bg-hp-carbon"}`}>
      <p className={`text-xs font-bold uppercase tracking-[0.2em] mb-3 ${accent ? "text-hp-yellow" : "text-neutral-400"}`}>{label}</p>
      <div className="flex items-baseline gap-2">
        <span className={`font-stat font-black text-5xl leading-none ${accent ? "text-hp-yellow" : "text-white"}`}>
          {hp == null ? "—" : animate ? <StatCounter to={hp} /> : hp}
        </span>
        <span className="font-stat font-bold text-lg text-neutral-400">CH</span>
      </div>
      <div className="flex items-baseline gap-2 mt-2">
        <span className="font-stat font-bold text-2xl text-white">{nm == null ? "N.C." : animate ? <StatCounter to={nm} /> : nm}</span>
        <span className="font-stat text-sm text-neutral-400">NM</span>
      </div>
    </div>
  );
}

function GainOnQuote({ vehicle, onRequest }) {
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="config-gain-on-quote">
      <StatBlock label="Origine" hp={vehicle.origin_hp} nm={vehicle.origin_nm} />
      <div className="p-6 border border-hp-yellow bg-hp-yellow/[0.06] flex flex-col justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-hp-yellow mb-2 flex items-center gap-2"><TrendingUp size={14} /> Cartographie sur mesure</p>
          <p className="text-sm text-neutral-300">Pour cette version, nos préparateurs calculent votre gain Stage 1, Stage 2 et E85 sur mesure, validé au banc de puissance. Votre centre vous répond rapidement.</p>
        </div>
        <button onClick={onRequest} className="hp-cta px-6 py-4 flex items-center justify-center gap-2" data-testid="config-request-gain">
          Recevoir mon gain personnalisé <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}

function GainBlock({ v }) {
  return (
    <div className="p-6 border border-white/10 bg-black flex flex-col justify-center">
      <div className="flex items-center gap-2 text-hp-yellow mb-3"><TrendingUp size={16} /><p className="text-xs font-bold uppercase tracking-[0.2em]">Gain Stage 1</p></div>
      <div className="flex items-baseline gap-2">
        <span className="font-stat font-black text-5xl text-hp-yellow">{v.origin_hp ? <>+<StatCounter to={v.stage1_hp - v.origin_hp} /></> : "N.C."}</span>
        <span className="font-stat text-lg text-neutral-400">CH</span>
      </div>
      <div className="flex items-baseline gap-2 mt-2">
        <span className="font-stat font-bold text-2xl text-white">{v.stage1_nm && v.origin_nm ? <>+<StatCounter to={v.stage1_nm - v.origin_nm} /></> : "N.C."}</span>
        <span className="font-stat text-sm text-neutral-400">NM</span>
      </div>
    </div>
  );
}

function E85Block({ v }) {
  return (
    <div className="p-6 border border-hp-yellow/40 bg-hp-carbon">
      <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3 text-hp-yellow flex items-center gap-2"><Zap size={14} /> E85 FlexFuel</p>
      <div className="flex items-baseline gap-2"><span className="font-stat font-black text-5xl text-white"><StatCounter to={v.e85_hp} /></span><span className="font-stat text-lg text-neutral-400">CH</span></div>
      <div className="flex items-baseline gap-2 mt-2"><span className="font-stat font-bold text-2xl text-white">{v.e85_nm ? <StatCounter to={v.e85_nm} /> : "N.C."}</span><span className="font-stat text-sm text-neutral-400">NM</span></div>
    </div>
  );
}

export default function ConfigResults({ vehicle: v, onRequest }) {
  if (!(v.stage1_hp || v.stage2_hp)) return <GainOnQuote vehicle={v} onRequest={() => onRequest("Demande de gain")} />;
  const blocks = [
    v.stage1_hp && <StatBlock key="s1" label="Stage 1" hp={v.stage1_hp} nm={v.stage1_nm} accent animate />,
    v.stage1_hp && <GainBlock key="g" v={v} />,
    v.stage2_hp && <StatBlock key="s2" label="Stage 2" hp={v.stage2_hp} nm={v.stage2_nm} accent={!v.stage1_hp} animate />,
    v.e85_hp && <E85Block key="e" v={v} />,
  ].filter(Boolean);
  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatBlock label="Origine" hp={v.origin_hp} nm={v.origin_nm} />
        {blocks}
        <div className={(1 + blocks.length) % 3 === 1 ? "sm:col-span-2" : (1 + blocks.length) % 3 === 2 ? "sm:col-span-2 lg:col-span-1" : "sm:col-span-2 lg:col-span-3"}>
          <DynoChart origin={v.origin_hp} stage1={v.stage1_hp} stage2={v.stage2_hp} originNm={v.origin_nm} stage1Nm={v.stage1_nm} />
        </div>
      </div>
      {v.options?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="config-options">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400 mr-2 self-center">Options</span>
          {v.options.map((o) => <span key={o} className="text-xs border border-white/15 px-2.5 py-1 text-neutral-300">{o}</span>)}
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        {v.stage1_hp && (
          <button onClick={() => onRequest("Stage 1")} className="hp-cta flex-1 px-6 py-4 flex items-center justify-center gap-2" data-testid="config-cta">
            Je veux le Stage 1 <ArrowRight size={18} />
          </button>
        )}
        {v.stage2_hp && (
          <button onClick={() => onRequest("Stage 2")} className="flex-1 px-6 py-4 flex items-center justify-center gap-2 border border-hp-yellow text-hp-yellow font-bold uppercase text-sm tracking-wide hover:bg-hp-yellow hover:text-black transition-colors" data-testid="config-cta-stage2">
            Je veux le Stage 2 <ArrowRight size={18} />
          </button>
        )}
      </div>
      <p className="text-xs text-neutral-500 mt-3">Données de puissance indicatives. Chaque cartographie est développée et validée au banc de puissance dans nos centres.</p>
    </>
  );
}
